import React from 'react';
import { Link } from 'react-router-dom';
import AnalysisFlowchart from './components/AnalysisFlowchart';
import RiskScoreGauge from './components/RiskScoreGauge';
import BeforeAfterExample from './components/BeforeAfterExample';

function HowItWorks() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-slate-800 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">How It Works</h1>
          <p className="text-lg md:text-xl text-blue-100">
            Understand the end-to-end analysis pipeline behind AdverseVideo Detector AI.
          </p>
        </div>
      </div>

      <main className="container mx-auto px-4 py-12 max-w-6xl space-y-10">
        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">Simple Analysis in 4 Steps</h2>

          <div className="space-y-6 text-gray-700">
            <div>
              <h3 className="text-xl font-semibold mb-2">Step 1: Submit Video URL</h3>
              <p>Paste any YouTube video URL into our analyzer. No account required.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Step 2: Multimodal AI Analysis</h3>
              <p className="mb-4">Our system performs comprehensive analysis:</p>

              <p className="font-semibold">Visual Analysis (GPT-4 Vision)</p>
              <ul className="list-disc list-inside mb-4">
                <li>Extracts 8-10 keyframes from video</li>
                <li>Analyzes each frame for threat indicators:</li>
                <li>Hacking tools (Kali Linux, Metasploit, Burp Suite)</li>
                <li>Terminal/command line interfaces</li>
                <li>Code editors showing exploits</li>
                <li>Network penetration diagrams</li>
                <li>Anonymous symbols/masks</li>
              </ul>

              <p className="font-semibold">Audio Analysis (Whisper AI)</p>
              <ul className="list-disc list-inside mb-4">
                <li>Transcribes spoken content</li>
                <li>Detects threat-related keywords</li>
                <li>Identifies instructional language about exploits</li>
                <li>Flags references to hacking tools</li>
              </ul>

              <p className="font-semibold">Metadata Analysis</p>
              <ul className="list-disc list-inside">
                <li>Title and description keyword matching</li>
                <li>Tag analysis for threat indicators</li>
                <li>Channel history and patterns</li>
                <li>Engagement metrics anomalies</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Step 3: Risk Scoring</h3>
              <p className="mb-3">Our algorithm combines all signals to calculate a 0-100 risk score:</p>
              <ul className="list-disc list-inside">
                <li><strong>0-25:</strong> Low Risk (Educational/legitimate content)</li>
                <li><strong>26-50:</strong> Medium Risk (Requires review)</li>
                <li><strong>51-75:</strong> High Risk (Potential threat content)</li>
                <li><strong>76-100:</strong> Critical Risk (Active threat indicators)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Step 4: Comprehensive Report</h3>
              <p className="mb-3">Receive detailed analysis including:</p>
              <ul className="list-disc list-inside">
                <li>Overall risk assessment</li>
                <li>Frame-by-frame visual analysis</li>
                <li>Full audio transcript analysis</li>
                <li>Threat keyword matches</li>
                <li>Downloadable PDF report</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">Our Analysis Process</h2>
          <AnalysisFlowchart />
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">Understanding Risk Scores</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            <RiskScoreGauge score={15} />
            <RiskScoreGauge score={35} />
            <RiskScoreGauge score={65} />
            <RiskScoreGauge score={90} />
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <BeforeAfterExample />
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Accuracy & Limitations</h2>

          <h3 className="text-xl font-semibold mb-2">Strengths:</h3>
          <ul className="list-disc list-inside mb-4 text-gray-700">
            <li>Multimodal approach (visual + audio + metadata)</li>
            <li>State-of-the-art AI models</li>
            <li>Research-validated methodology</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">Limitations:</h3>
          <ul className="list-disc list-inside text-gray-700">
            <li>Does not guarantee 100% accuracy</li>
            <li>Designed for research/educational use</li>
            <li>Not a replacement for human expert analysis</li>
            <li>May produce false positives/negatives</li>
          </ul>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Use Cases</h2>

          <h3 className="text-xl font-semibold mb-2">Academic Research</h3>
          <ul className="list-disc list-inside mb-4 text-gray-700">
            <li>Study cyber threat dissemination patterns</li>
            <li>Analyze video-based radicalization</li>
            <li>Research social media threat landscapes</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">Educational Institutions</h3>
          <ul className="list-disc list-inside mb-4 text-gray-700">
            <li>Vet content before using in classrooms</li>
            <li>Protect students from harmful tutorials</li>
            <li>Monitor security awareness training materials</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">Security Professionals</h3>
          <ul className="list-disc list-inside mb-4 text-gray-700">
            <li>Threat intelligence collection</li>
            <li>Open-source intelligence (OSINT)</li>
            <li>Content moderation support</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">Organizations</h3>
          <ul className="list-disc list-inside text-gray-700">
            <li>Validate training materials</li>
            <li>Monitor employee-shared content</li>
            <li>Risk assessment for user-generated content</li>
          </ul>
        </section>

        <section className="text-center pt-2 pb-8">
          <div className="space-x-3">
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Back to Home
          </Link>
          <Link
            to="/faq"
            className="inline-block bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition"
          >
            FAQ
          </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default HowItWorks;
